import {
  Bot,
  CircleDashed,
  Database,
  FileCheck2,
  Gauge,
  ListChecks,
  LoaderCircle,
  LogOut,
  PlayCircle,
  RefreshCcw,
  Search,
  Settings,
  ShieldCheck,
  UserRound,
  UsersRound,
  Video
} from "lucide-react";
import { useEffect, useMemo, useState, type ComponentType } from "react";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "~/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from "~/components/ui/empty";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "~/components/ui/table";
import type {
  CreatorPostListResponse,
  CreatorListResponse,
  CurrentContextResponse,
  HealthResponse,
  StorypilotDiagnosticsResponse,
  TikTokAccountListResponse
} from "../shared/contracts";
import {
  loadCreators,
  loadCurrentContext,
  loadCreatorPosts,
  loadHealth,
  loadStorypilotDiagnostics,
  loadTikTokAccounts,
  logoutProductSession,
  readFixtureSession,
  runStorypilotFixtureFlow,
  seedResearchCreators,
  storyOsLoginUrl,
  writeFixtureSession
} from "./api";

type LoadState<T> =
  | { status: "loading" }
  | { status: "ready"; data: T }
  | { status: "error"; message: string };

type AppData = {
  health: HealthResponse;
  context: CurrentContextResponse | null;
  diagnostics: StorypilotDiagnosticsResponse | null;
  creators: CreatorListResponse | null;
  accounts: TikTokAccountListResponse | null;
  posts: CreatorPostListResponse | null;
};

type ActionState =
  | { status: "idle" }
  | { status: "running"; label: string }
  | { status: "success"; message: string }
  | { status: "error"; message: string };

const navigation = [
  { label: "Dashboard", icon: Gauge },
  { label: "Creators", icon: UsersRound },
  { label: "Accounts", icon: UserRound },
  { label: "Posts", icon: Video },
  { label: "Review", icon: ListChecks },
  { label: "Cycles", icon: FileCheck2 },
  { label: "Settings", icon: Settings }
];

function unwrap<T>(envelope: { ok: true; data: T } | { ok: false; error: { message: string } }): T {
  if (!envelope.ok) {
    throw new Error(envelope.error.message);
  }

  return envelope.data;
}

async function loadAppData(): Promise<AppData> {
  const health = unwrap(await loadHealth());
  const contextEnvelope = await loadCurrentContext();
  if (!contextEnvelope.ok) {
    return {
      health,
      context: null,
      diagnostics: null,
      creators: null,
      accounts: null,
      posts: null
    };
  }

  const [diagnostics, creators, accounts, posts] = await Promise.all([
    loadStorypilotDiagnostics(),
    loadCreators(),
    loadTikTokAccounts(),
    loadCreatorPosts()
  ]);

  return {
    health,
    context: contextEnvelope.data,
    diagnostics: unwrap(diagnostics),
    creators: unwrap(creators),
    accounts: unwrap(accounts),
    posts: unwrap(posts)
  };
}

function useAppData(refreshKey: number): LoadState<AppData> {
  const [state, setState] = useState<LoadState<AppData>>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });

    loadAppData()
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data });
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : "Unable to load TikTok SRP Ops.";
          setState({ status: "error", message });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return state;
}

function hasFixtureDebugFlag() {
  if (typeof window === "undefined") {
    return false;
  }

  return new URLSearchParams(window.location.search).has("debugFixture");
}

export function App() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [fixtureSession, setFixtureSession] = useState<string | null>(() => readFixtureSession());
  const [actionState, setActionState] = useState<ActionState>({ status: "idle" });
  const state = useAppData(refreshKey);

  function refresh() {
    setRefreshKey((value) => value + 1);
  }

  function setDebugFixtureSession(nextSession: string | null) {
    writeFixtureSession(nextSession);
    setFixtureSession(nextSession);
    refresh();
  }

  async function signOut() {
    writeFixtureSession(null);
    setFixtureSession(null);
    await logoutProductSession();
    setActionState({ status: "idle" });
    refresh();
  }

  async function runAction(
    label: string,
    action: () => Promise<string>
  ) {
    setActionState({ status: "running", label });
    try {
      const message = await action();
      setActionState({ status: "success", message });
      refresh();
    } catch (error: unknown) {
      setActionState({
        status: "error",
        message: error instanceof Error ? error.message : "Action failed."
      });
    }
  }

  async function seedAccounts() {
    await runAction("Seeding accounts", async () => {
      const result = unwrap(await seedResearchCreators());
      return `Seeded ${result.creatorsImported} creators and ${result.accountsImported} TikTok accounts.`;
    });
  }

  async function runPocSync() {
    await runAction("Syncing Storypilot", async () => {
      const result = unwrap(await runStorypilotFixtureFlow());
      return `Synced ${result.counts.accountsAdmitted} accounts and ${result.counts.postsSynced} posts.`;
    });
  }

  const data = state.status === "ready" ? state.data : null;
  if (!data?.context) {
    return (
      <ProductLoginScreen
        state={state}
        fixtureSession={fixtureSession}
        onDebugFixtureSessionChange={setDebugFixtureSession}
        onRefresh={refresh}
      />
    );
  }

  return (
    <ProductWorkspace
      data={data}
      actionState={actionState}
      onRefresh={refresh}
      onSeedAccounts={seedAccounts}
      onRunPocSync={runPocSync}
      onSignOut={signOut}
    />
  );
}

function ProductWorkspace({
  data,
  actionState,
  onRefresh,
  onSeedAccounts,
  onRunPocSync,
  onSignOut
}: {
  data: AppData;
  actionState: ActionState;
  onRefresh: () => void;
  onSeedAccounts: () => Promise<void>;
  onRunPocSync: () => Promise<void>;
  onSignOut: () => void;
}) {
  const isActionRunning = actionState.status === "running";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[16rem_minmax(0,1fr)]">
        <aside className="border-border bg-sidebar text-sidebar-foreground flex min-h-0 flex-col border-r px-4 py-5">
          <div className="flex items-center gap-3 px-2">
            <div className="bg-primary text-primary-foreground flex size-10 shrink-0 items-center justify-center rounded-md text-sm font-semibold">
              SRP
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">TikTok SRP Ops</div>
              <div className="text-muted-foreground truncate text-xs">Specialized Rewards</div>
            </div>
          </div>

          <nav className="mt-8 grid gap-1" aria-label="Product navigation">
            {navigation.map((item) => (
              <Button
                key={item.label}
                type="button"
                variant={item.label === "Dashboard" ? "secondary" : "ghost"}
                size="sm"
                className="w-full justify-start"
                leftIcon={<item.icon aria-hidden="true" />}
              >
                {item.label}
              </Button>
            ))}
          </nav>

          <div className="mt-auto pt-6">
            <Badge variant="success" className="w-full justify-start gap-2">
              <ShieldCheck aria-hidden="true" />
              StoryOS Session
            </Badge>
          </div>
        </aside>

        <main className="min-w-0">
          <header className="border-border bg-background/95 sticky top-0 z-10 flex flex-col gap-4 border-b px-5 py-4 backdrop-blur md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <h1 className="text-xl font-semibold tracking-normal">SRP Operations</h1>
              <p className="text-muted-foreground mt-1 text-sm">
                Creator intake, TikTok account coverage, Storypilot contract health, and review readiness.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                leftIcon={<UsersRound aria-hidden="true" />}
                onClick={() => void onSeedAccounts()}
                disabled={isActionRunning}
              >
                Seed Accounts
              </Button>
              <Button
                type="button"
                size="sm"
                leftIcon={
                  isActionRunning ? (
                    <LoaderCircle aria-hidden="true" className="animate-spin" />
                  ) : (
                    <PlayCircle aria-hidden="true" />
                  )
                }
                onClick={() => void onRunPocSync()}
                disabled={isActionRunning}
              >
                Run PoC Sync
              </Button>
              <Button type="button" variant="outline" size="sm" leftIcon={<RefreshCcw aria-hidden="true" />} onClick={onRefresh}>
                Refresh
              </Button>
              <Button type="button" variant="secondary" size="sm" leftIcon={<LogOut aria-hidden="true" />} onClick={onSignOut}>
                Sign Out
              </Button>
            </div>
          </header>

          <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 px-5 py-5">
            <ActionNotice state={actionState} />
            <Dashboard data={data} />
          </div>
        </main>
      </div>
    </div>
  );
}

function ActionNotice({ state }: { state: ActionState }) {
  if (state.status === "idle") {
    return null;
  }

  if (state.status === "running") {
    return (
      <div className="border-border bg-card text-card-foreground flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
        <LoaderCircle aria-hidden="true" className="size-4 animate-spin text-muted-foreground" />
        <span>{state.label}</span>
      </div>
    );
  }

  return (
    <div
      className={
        state.status === "success"
          ? "border-border bg-card text-card-foreground rounded-md border px-3 py-2 text-sm"
          : "border-destructive/40 bg-card text-destructive rounded-md border px-3 py-2 text-sm"
      }
    >
      {state.message}
    </div>
  );
}

function ProductLoginScreen({
  state,
  fixtureSession,
  onDebugFixtureSessionChange,
  onRefresh
}: {
  state: LoadState<AppData>;
  fixtureSession: string | null;
  onDebugFixtureSessionChange: (session: string | null) => void;
  onRefresh: () => void;
}) {
  const health = state.status === "ready" ? state.data.health : null;
  const showFixtureDebug = Boolean(health?.auth.fixtureDebugEnabled) && hasFixtureDebugFlag();
  const errorMessage = state.status === "error" ? state.message : null;
  const isCheckingSession = state.status === "loading";

  return (
    <main className="flex min-h-svh items-center justify-center bg-background px-4 py-8 text-foreground sm:px-6">
      <div className="flex w-full max-w-[526px] flex-col items-center gap-6">
        <TikTokSrpLoginBrand />

        <section
          aria-labelledby="tiktok-srp-auth-title"
          className="w-full max-w-[400px] overflow-hidden rounded-md border border-border bg-card text-card-foreground shadow-none"
        >
          <header className="grid gap-1 p-6 text-center">
            <h1 id="tiktok-srp-auth-title" className="m-0 text-3xl font-semibold tracking-normal text-foreground">
              Sign in
            </h1>
            <p className="m-0 text-sm text-muted-foreground">Use SSO to access TikTok SRP.</p>
          </header>

          <div className="grid gap-4 px-6 pb-6">
            <Button
              type="button"
              className="w-full"
              leftIcon={<Microsoft365Mark />}
              onClick={() => window.location.assign(storyOsLoginUrl())}
            >
              Sign in with Microsoft 365
            </Button>
            {isCheckingSession ? (
              <div className="text-muted-foreground flex items-center justify-center gap-2 text-sm">
                <LoaderCircle aria-hidden="true" className="size-4 animate-spin" />
                Checking existing session
              </div>
            ) : null}
            {errorMessage ? (
              <div className="grid gap-2 text-center">
                <p className="m-0 text-sm text-destructive">{errorMessage}</p>
                <Button type="button" variant="outline" size="sm" onClick={onRefresh}>
                  Retry
                </Button>
              </div>
            ) : null}
            {showFixtureDebug ? (
              <div className="rounded-md border border-dashed border-border p-3">
                <div className="text-sm font-medium">Fixture debug</div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Fixture auth is only available for local test/debug sessions and is not a Product SSO substitute.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant={fixtureSession === "operator" ? "secondary" : "outline"}
                    size="sm"
                    leftIcon={<ShieldCheck aria-hidden="true" />}
                    onClick={() => onDebugFixtureSessionChange("operator")}
                  >
                    Operator Fixture
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    leftIcon={<LogOut aria-hidden="true" />}
                    onClick={() => onDebugFixtureSessionChange(null)}
                  >
                    Clear Fixture
                  </Button>
                </div>
              </div>
            ) : null}
          </div>

          <footer className="px-6 pt-0 pb-6 text-center">
            <p className="m-0 text-xs text-muted-foreground">Trouble signing in?</p>
            <p className="m-0 text-xs text-muted-foreground">Contact your StoryOS administrator.</p>
          </footer>
        </section>
      </div>
    </main>
  );
}

function TikTokSrpLoginBrand() {
  return (
    <div className="flex items-center gap-3" aria-label="TikTok SRP">
      <div className="bg-primary text-primary-foreground flex size-12 shrink-0 items-center justify-center rounded-md text-sm font-semibold">
        SRP
      </div>
      <div className="text-4xl font-semibold tracking-normal text-foreground">TikTok SRP</div>
    </div>
  );
}

function Microsoft365Mark() {
  return (
    <span className="grid size-4 grid-cols-2 gap-px" aria-hidden="true">
      <span className="bg-[#f25022]" />
      <span className="bg-[#7fba00]" />
      <span className="bg-[#00a4ef]" />
      <span className="bg-[#ffb900]" />
    </span>
  );
}

function Dashboard({
  data
}: {
  data: AppData;
}) {
  if (!data.context) {
    return null;
  }

  const accountCount = data.accounts?.items.length ?? 0;
  const creatorCount = data.creators?.items.length ?? 0;
  const postCount = data.posts?.items.length ?? 0;
  const mediaReady = data.posts?.items.filter((post) => post.mediaProviderReadable).length ?? 0;

  return (
    <>
      <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" aria-label="SRP Ops summary">
        <MetricCard label="Creators" value={creatorCount.toString()} detail="CRM rows in product D1" icon={UsersRound} />
        <MetricCard label="Accounts" value={accountCount.toString()} detail="TikTok mappings under review" icon={UserRound} />
        <MetricCard label="Posts" value={postCount.toString()} detail="Local ledger rows from Storypilot" icon={Video} />
        <MetricCard
          label="Media"
          value={postCount > 0 ? `${mediaReady}/${postCount}` : "0"}
          detail="Storypilot-owned media references"
          icon={Database}
        />
      </section>

      <section className="grid gap-5 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle>Current Context</CardTitle>
                <CardDescription>StoryOS Product SSO tenant and role resolution.</CardDescription>
              </div>
              <StatusBadge label={data.context.session.targetOrganization.name} tone="success" icon={ShieldCheck} />
            </div>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-4 text-sm sm:grid-cols-2">
              <Definition label="Actor" value={data.context.session.actor.displayName} />
              <Definition label="Home Organization" value={data.context.session.actor.homeOrganizationName} />
              <Definition label="Roles" value={data.context.session.roles.join(", ")} />
              <Definition label="Runtime" value={data.context.runtime.environment} />
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle>Storypilot Contract</CardTitle>
                <CardDescription>Generic ingestion and AI-analysis surfaces this product can call.</CardDescription>
              </div>
              <StatusBadge label={data.diagnostics?.mode ?? "fixture"} tone="muted" icon={Bot} />
            </div>
          </CardHeader>
          <CardContent>
            <ContractList diagnostics={data.diagnostics} />
          </CardContent>
        </Card>
      </section>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>Creators</CardTitle>
              <CardDescription>Creator CRM seed for the 10-account PoC.</CardDescription>
            </div>
            <StatusBadge label="PoC seed" tone="muted" icon={Search} />
          </div>
        </CardHeader>
        <CardContent>
          <CreatorTable creators={data.creators} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>TikTok Accounts</CardTitle>
              <CardDescription>Account mapping and scrape desired state handed to Storypilot.</CardDescription>
            </div>
            <StatusBadge label="Scrape Desired State" tone="muted" icon={PlayCircle} />
          </div>
        </CardHeader>
        <CardContent>
          <AccountTable accounts={data.accounts} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>Posts</CardTitle>
              <CardDescription>Storypilot post and media readback stored in the TikTok SRP ledger.</CardDescription>
            </div>
            <StatusBadge label="Post Journey" tone="muted" icon={ListChecks} />
          </div>
        </CardHeader>
        <CardContent>
          <PostTable posts={data.posts} />
        </CardContent>
      </Card>
    </>
  );
}

function ContractList({ diagnostics }: { diagnostics: StorypilotDiagnosticsResponse | null }) {
  const rows = useMemo(() => {
    if (!diagnostics) {
      return [];
    }

    return [
      ["Account Upsert", diagnostics.contract.accountUpsert],
      ["Scrape State", diagnostics.contract.scrapeState],
      ["Post List", diagnostics.contract.postList],
      ["AI Start", diagnostics.contract.aiVideoAnalysisStart],
      ["AI Execution", diagnostics.contract.aiVideoAnalysisExecution]
    ];
  }, [diagnostics]);

  if (rows.length === 0) {
    return <EmptyState title="No Storypilot contract diagnostics" description="Diagnostics load after a valid product session is present." />;
  }

  return (
    <div className="grid gap-3">
      {rows.map(([label, value]) => (
        <div key={label} className="grid gap-1 rounded-md border border-border px-3 py-2">
          <div className="text-xs font-medium text-muted-foreground">{label}</div>
          <code className="break-all text-xs text-foreground">{value}</code>
        </div>
      ))}
    </div>
  );
}

function CreatorTable({ creators }: { creators: CreatorListResponse | null }) {
  if (!creators || creators.items.length === 0) {
    return <EmptyState title="No creators yet" description="Seed accounts to create the initial creator CRM rows." />;
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Vertical</TableHead>
            <TableHead>Research Result</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Updated</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {creators.items.map((creator) => (
            <TableRow key={creator.id}>
              <TableCell className="font-medium">{creator.displayName}</TableCell>
              <TableCell>{creator.primaryVertical ?? "Unassigned"}</TableCell>
              <TableCell>{creator.sourceResearchResult ?? "Not seeded"}</TableCell>
              <TableCell>
                <StatusBadge label={creator.status} tone={creator.status === "active" ? "success" : "muted"} />
              </TableCell>
              <TableCell>{formatDate(creator.updatedAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function AccountTable({ accounts }: { accounts: TikTokAccountListResponse | null }) {
  if (!accounts || accounts.items.length === 0) {
    return <EmptyState title="No TikTok accounts yet" description="Seed accounts to create TikTok account mappings." />;
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Handle</TableHead>
            <TableHead>Scrape</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Post Journey</TableHead>
            <TableHead>Dataset</TableHead>
            <TableHead>Storypilot</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {accounts.items.map((account) => (
            <TableRow key={account.id}>
              <TableCell className="font-medium">@{account.handle}</TableCell>
              <TableCell>
                <StatusBadge
                  label={account.scrapeDesiredState}
                  tone={account.scrapeDesiredState === "enabled" ? "success" : "muted"}
                />
              </TableCell>
              <TableCell>{account.accountStatus}</TableCell>
              <TableCell>{account.storypilotPostJourneyStatus ?? "Pending"}</TableCell>
              <TableCell>{shortenId(account.storypilotDatasetId)}</TableCell>
              <TableCell>{shortenId(account.storypilotAccountId)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function PostTable({ posts }: { posts: CreatorPostListResponse | null }) {
  if (!posts || posts.items.length === 0) {
    return <EmptyState title="No posts yet" description="Run PoC Sync to pull Storypilot post and media readback into the ledger." />;
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Account</TableHead>
            <TableHead>Caption</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Views</TableHead>
            <TableHead>Media</TableHead>
            <TableHead>Updated</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {posts.items.slice(0, 20).map((post) => (
            <TableRow key={post.id}>
              <TableCell className="font-medium">@{post.accountHandle}</TableCell>
              <TableCell className="max-w-[24rem] truncate">{post.caption ?? post.platformExternalId}</TableCell>
              <TableCell>{post.durationSeconds ? `${post.durationSeconds}s` : "-"}</TableCell>
              <TableCell>{formatNumber(post.viewCount)}</TableCell>
              <TableCell>
                <StatusBadge
                  label={post.mediaProviderReadable ? "Ready" : "Pending"}
                  tone={post.mediaProviderReadable ? "success" : "muted"}
                />
              </TableCell>
              <TableCell>{formatDate(post.updatedAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function MetricCard({
  label,
  value,
  detail,
  icon: Icon
}: {
  label: string;
  value: string;
  detail: string;
  icon: ComponentType<{ "aria-hidden"?: boolean }>;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardDescription>{label}</CardDescription>
            <CardTitle className="mt-2 text-2xl">{value}</CardTitle>
          </div>
          <div className="bg-muted text-muted-foreground flex size-9 items-center justify-center rounded-md">
            <Icon aria-hidden={true} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 text-sm text-muted-foreground">{detail}</CardContent>
    </Card>
  );
}

function Definition({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-foreground">{value}</dd>
    </div>
  );
}

function StatusBadge({
  label,
  tone,
  icon: Icon
}: {
  label: string;
  tone: "success" | "muted";
  icon?: ComponentType<{ "aria-hidden"?: boolean }>;
}) {
  return (
    <Badge variant={tone === "success" ? "success" : "default"} className="gap-1.5 whitespace-nowrap">
      {Icon ? <Icon aria-hidden={true} /> : null}
      {label}
    </Badge>
  );
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <Empty>
      <EmptyMedia variant="icon">
        <CircleDashed aria-hidden="true" />
      </EmptyMedia>
      <EmptyHeader>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}

function formatNumber(value: number | null): string {
  if (value === null) {
    return "-";
  }

  return new Intl.NumberFormat("en", { notation: "compact" }).format(value);
}

function shortenId(value: string | null): string {
  if (!value) {
    return "Not linked";
  }

  if (value.length <= 24) {
    return value;
  }

  return `${value.slice(0, 21)}...`;
}
